import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Card, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiSave, FiX } from "react-icons/fi";
import Swal from 'sweetalert2';

export default function RoleCreate() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [formData, setFormData] = useState({
        name: "", // Image ke mutabiq default
        permissions: []
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/roles-create`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.permissions) {
                setPermissions(res.data.permissions);
                // Shuru mein sab select karne ke liye (jaisa image mein hai):
                setFormData(prev => ({ ...prev, permissions: res.data.permissions.map(p => p.name) }));
            }
        } catch (err) {
            console.error("Error fetching permissions:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (permissionName) => {
        setFormData(prev => {
            const updatedPermissions = prev.permissions.includes(permissionName)
                ? prev.permissions.filter(p => p !== permissionName)
                : [...prev.permissions, permissionName];
            return { ...prev, permissions: updatedPermissions };
        });
    };

    // Add the missing handleSubmit function
    const handleSubmit = async () => {
        // Validate form
        if (!formData.name.trim()) {
            setErrors({ name: t("Role name is required") });
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/roles`,
                {
                    name: formData.name,
                    permissions: formData.permissions
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: t('Success'),
                    text: t('Role created successfully'),
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate("/users-roles");
            } else {
                throw new Error(response.data.message || 'Failed to create role');
            }
        } catch (err) {
            console.error("Error creating role:", err);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: err.response?.data?.message || t('Failed to create role')
            });
            
            // Handle validation errors from server
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row className="justify-content-center">
                <Col xl={11}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            <Form onSubmit={(e) => e.preventDefault()}>
                                {/* Role Name Input */}
                                <Row className="align-items-center mb-4">
                                    <Col md={2}>
                                        <Form.Label className="fw-bold mb-0">
                                            {t("Role Name")} <span className="text-danger">*</span>
                                        </Form.Label>
                                    </Col>
                                    <Col md={10}>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!errors.name}
                                            style={{ borderRadius: '8px' }}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Row>

                                <h5 className="mb-3 fw-bold" style={{ color: '#444' }}>{t("Permissions")}</h5>

                                {/* Permissions Table */}
                                <div className="border rounded overflow-hidden">
                                    <Table hover responsive className="mb-0 custom-permission-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="py-3 ps-4  text-uppercase small fw-bold" style={{ width: '80%' }}>
                                                    {t("MODULE")}
                                                </th>
                                                <th className="py-3 text-uppercase small fw-bold text-center">
                                                    {t("MANAGE")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {permissions.map((permission) => (
                                                <tr key={permission.id}>
                                                    <td className="ps-4 py-3 text-muted">
                                                        {permission.name.replace(/_/g, ' ')}
                                                    </td>
                                                    <td className="text-center py-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="custom-checkbox-input"
                                                            checked={formData.permissions.includes(permission.name)}
                                                            onChange={() => handlePermissionChange(permission.name)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Form Actions */}
                                <div className="d-flex gap-2 mt-4">
                                    <ButtonComponent
                                        className="btn btn-primary px-4"
                                        text={<><FiSave className="me-2" /> {t("Save Role")}</>}
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                    />
                                    <ButtonComponent
                                        className="btn btn-info border px-4"
                                        text={<><FiX className="me-2" /> {t("Cancel")}</>}
                                        onClick={() => navigate("/users-roles")}
                                    />
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Custom CSS for Image Matching */}
            <style jsx>{`
                .custom-permission-table thead th {
                    background-color: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    letter-spacing: 0.5px;
                }
                .custom-permission-table td {
                    border-bottom: 1px solid #f0f0f0;
                    vertical-align: middle;
                    font-size: 15px;
                }
                .form-check-input:checked {
                    background-color: #a855f7; /* Purple color as seen in image */
                    border-color: #a855f7;
                }
                .form-control:focus {
                    box-shadow: none;
                    border-color: #dee2e6;
                }
            `}</style>
        </PageLayout>
    );
}